import React from "react";
import { Chip, Tooltip } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon,
  LinkOff as LinkOffIcon,
} from "@mui/icons-material";

/**
 * SyncBadge — renders a MUI Chip summarizing a client's app-catalog sync state.
 *
 * Severity buckets:
 *   - null metabaseDatabaseId → "Unmapped — no Metabase DB assigned" (warning/yellow)
 *   - lastSyncAt is null      → "Never synced" (grey/default)
 *   - < 90 min ago            → "Synced Nm ago" (success/green)
 *   - < 24h                   → "Synced Nh ago" (warning/amber)
 *   - >= 24h                  → "Synced Nd ago" (error/red)
 */
const MIN_MS = 60 * 1000;
const HOUR_MS = 60 * MIN_MS;
const DAY_MS = 24 * HOUR_MS;

function formatRelative(then) {
  const diff = Date.now() - then.getTime();
  if (diff < MIN_MS) return "just now";
  if (diff < HOUR_MS) return `${Math.floor(diff / MIN_MS)} min ago`;
  if (diff < DAY_MS) return `${Math.floor(diff / HOUR_MS)} hours ago`;
  return `${Math.floor(diff / DAY_MS)} days ago`;
}

export function computeSyncState({ lastSyncAt, metabaseDatabaseId }) {
  if (metabaseDatabaseId == null || metabaseDatabaseId === undefined) {
    return {
      severity: "unmapped",
      color: "warning",
      icon: "unmapped",
      label: "Unmapped — no Metabase DB assigned",
      tooltip:
        "This client has no Metabase database assigned. App catalog sync falls through to the local mock path.",
    };
  }

  if (!lastSyncAt) {
    return {
      severity: "never",
      color: "default",
      icon: "never",
      label: "Never synced",
      tooltip: "No successful sync recorded yet.",
    };
  }

  const then = new Date(lastSyncAt);
  const diff = Date.now() - then.getTime();
  const rel = formatRelative(then);
  const iso = then.toLocaleString();

  if (diff < 90 * MIN_MS) {
    return {
      severity: "fresh",
      color: "success",
      icon: "ok",
      label: `Synced ${rel}`,
      tooltip: `Last sync: ${iso}`,
    };
  }
  if (diff < DAY_MS) {
    return {
      severity: "stale",
      color: "warning",
      icon: "warn",
      label: `Synced ${rel}`,
      tooltip: `Last sync: ${iso} (over 90 min — consider refresh)`,
    };
  }
  return {
    severity: "old",
    color: "error",
    icon: "err",
    label: `Synced ${rel}`,
    tooltip: `Last sync: ${iso} (stale — run sync now)`,
  };
}

const ICONS = {
  ok: <CheckCircleIcon />,
  warn: <WarningIcon />,
  err: <ErrorIcon />,
  never: <HelpOutlineIcon />,
  unmapped: <LinkOffIcon />,
};

const SyncBadge = ({ lastSyncAt, metabaseDatabaseId, size = "small", variant = "outlined" }) => {
  const state = computeSyncState({ lastSyncAt, metabaseDatabaseId });
  return (
    <Tooltip title={state.tooltip} arrow>
      <Chip
        data-testid="sync-badge"
        data-severity={state.severity}
        icon={ICONS[state.icon]}
        label={state.label}
        size={size}
        color={state.color}
        variant={variant}
      />
    </Tooltip>
  );
};

export default SyncBadge;
